'use client';

import { useEffect, useState } from 'react';
import { Icon } from '../icon';
import { CountUp, toneVar, useReveal } from '../primitives';
import { href, lifecycle, stats } from '../data';

/** Vertical animated pipeline: a comet travels the rail; each stage lights in turn. */
export function LifecyclePipeline() {
  const stages = lifecycle;
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setActive((a) => (a + 1) % stages.length), 1900);
    return () => clearInterval(t);
  }, [stages.length]);
  return (
    <div className="pipe card" style={{ position: 'relative', padding: '24px 22px', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(420px 220px at 90% -10%, oklch(0.5 0.17 258 / 0.25), transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <div className="between" style={{ marginBottom: 18, position: 'relative' }}>
        <span className="pill">
          <span className="pill-dot" style={{ animation: 'pulse-dot 1.8s infinite' }} />
          Live · 7 agents working
        </span>
        <span className="mono subtle" style={{ fontSize: 11 }}>
          one transaction
        </span>
      </div>
      <div style={{ position: 'relative' }}>
        {/* rail */}
        <div style={{ position: 'absolute', left: 23, top: 8, bottom: 8, width: 2, background: 'var(--line)', borderRadius: 2 }} />
        <div className="pipe-comet" />
        <div className="stack" style={{ gap: 12, position: 'relative' }}>
          {stages.map((s, i) => {
            const c = toneVar(s.tone);
            const on = i === active;
            return (
              <div
                key={s.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '48px minmax(0,1fr)',
                  gap: 14,
                  alignItems: 'center',
                  opacity: on ? 1 : 0.62,
                  transition: 'opacity .5s',
                }}
              >
                <span
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 13,
                    display: 'grid',
                    placeItems: 'center',
                    color: c,
                    zIndex: 1,
                    background: on ? `color-mix(in oklch, ${c} 26%, var(--bg-1))` : 'var(--bg-1)',
                    border: `1px solid ${on ? c : 'var(--line)'}`,
                    boxShadow: on ? `0 0 0 5px color-mix(in oklch, ${c} 14%, transparent)` : 'none',
                    transition: 'box-shadow .5s, border-color .5s, background .5s',
                  }}
                >
                  <Icon name={s.icon} size={22} strokeWidth={1.8} />
                </span>
                <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="mono" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: c }}>
                      {s.label}
                    </span>
                    {s.isNew && (
                      <span className="tag tag-green" style={{ height: 18, fontSize: 9.5 }}>
                        New
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13.5, color: 'var(--fg-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.stat.fig} · {s.stat.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rule" style={{ margin: '18px 0 14px' }} />
      <div className="between" style={{ fontSize: 12.5 }}>
        <span className="subtle">Outcome</span>
        <span className="row" style={{ gap: 7, color: 'var(--green)', fontWeight: 500 }}>
          <Icon name="circle-check" size={15} />
          Dispute resolved before chargeback
        </span>
      </div>
    </div>
  );
}

export function Hero() {
  return (
    <section style={{ position: 'relative', paddingTop: 132, paddingBottom: 'clamp(48px, 8vw, 96px)' }}>
      <div className="grid-bg" style={{ height: '120%' }} />
      <div className="container" style={{ position: 'relative' }}>
        <div
          className="hero-grid"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.08fr) minmax(0, 0.92fr)', gap: 'clamp(32px, 5vw, 72px)', alignItems: 'center' }}
        >
          <div className="stack hero-copy" style={{ gap: 26 }}>
            <span className="pill" style={{ alignSelf: 'flex-start' }}>
              <Icon name="sparkles" size={14} style={{ color: 'var(--primary-2)' }} />
              Now with the agentic Representment Engine
            </span>
            <h1 className="display balance">
              The whole dispute lifecycle,
              <br />
              <span className="grad-text">working on its own.</span>
            </h1>
            <p className="lead pretty" style={{ maxWidth: 540 }}>
              FRAUDefender prevents, resolves, surfaces and recovers — across Visa and Mastercard — through one integration to the
              global issuer network. Stop chargebacks before they form, and let agents fight the ones worth fighting.
            </p>
            <div className="row wrap" style={{ gap: 12 }}>
              <a href={href('enroll')} className="btn btn-primary btn-lg">
                Start enrollment
                <Icon name="arrow-right" size={18} />
              </a>
              <a href={href('engine')} className="btn btn-ghost btn-lg">
                <Icon name="play" size={16} />
                See the engine work
              </a>
            </div>
            <div className="row wrap" style={{ gap: '14px 26px', marginTop: 4 }}>
              {([
                ['shield-check', 'Identify'],
                ['lock', 'Protect'],
                ['file-signature', 'Defend'],
              ] as [string, string][]).map(([ic, t]) => (
                <span key={t} className="row subtle" style={{ gap: 8, fontSize: 13.5 }}>
                  <Icon name={ic} size={16} style={{ color: 'var(--primary-2)' }} />
                  {t}
                </span>
              ))}
              <span className="row subtle" style={{ gap: 8, fontSize: 13.5 }}>
                <Icon name="globe" size={16} style={{ color: 'var(--primary-2)' }} />
                99%+ issuer reach
              </span>
            </div>
          </div>
          <div className="hero-visual" style={{ position: 'relative' }}>
            <LifecyclePipeline />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Compact KPI strip under the hero. */
export function StatsStrip() {
  useReveal();
  return (
    <section className="section-tight" style={{ paddingTop: 0 }}>
      <div className="container">
        <div className="card reveal" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', overflow: 'hidden' }} id="stats-strip">
          {stats.map((s, i) => {
            const c = toneVar(s.tone);
            return (
              <div key={i} className="stack" style={{ gap: 10, padding: 'clamp(20px,2.6vw,30px)', borderLeft: i ? '1px solid var(--line-soft)' : 'none' }}>
                <Icon name={s.icon} size={20} style={{ color: c }} />
                <div className="stat-fig" style={{ color: 'var(--fg)' }}>
                  <CountUp to={s.fig} decimals={s.decimals} suffix={s.suffix} />
                </div>
                <div className="stack" style={{ gap: 2 }}>
                  <div style={{ fontSize: 13.5, color: 'var(--fg-soft)' }}>{s.label}</div>
                  <div className="subtle" style={{ fontSize: 12 }}>
                    {s.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
